import { memo, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react'
import { APP_SETTINGS } from '../config/appSettings'
import { isUsableUrlTitle, urlDisplayTitle, type ContentType, type UrlEntity } from '../domain/note'
import { linkPreviewPresentation } from '../links/linkPreviewCategory'
import {
  deriveLinkMetadataStatus,
  hasExhaustedLinkMetadataRetries,
  hasSpecificLinkImage,
  linkPreviewImageUrl,
  type LinkMetadataStatus,
} from '../links/linkMetadata'
import { copyText } from '../utils/clipboard'
import { hideMarkdownPreviewArtifacts } from '../utils/markdownText'
import { Icon } from './Icon'
import { previewSpringFrames } from '../utils/previewSpring'

interface LinkPreviewProps {
  readOnly?: boolean
  contentType: ContentType
  fallbackTitle?: string
  hideTitle?: boolean
  matchTerms?: readonly string[]
  metadataStatus?: LinkMetadataStatus | null
  onRetryMetadata?: (url: UrlEntity) => void
  onRemove?: (url: UrlEntity) => void
  preserveArtworkOnFailure?: boolean
  removing?: boolean
  url: UrlEntity
  variant?: 'full' | 'compact' | 'collection'
}

export function formatDuration(durationSeconds: number): string {
  if (durationSeconds >= 3_600) {
    const hours = Math.floor(durationSeconds / 3_600)
    const minutes = Math.round((durationSeconds % 3_600) / 60)
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
  const minutes = Math.floor(durationSeconds / 60)
  const seconds = Math.round(durationSeconds % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function displayUrl(url: UrlEntity, matchTerms: readonly string[] = []): string {
  try {
    const parsed = new URL(url.canonicalUrl ?? url.originalUrl)
    const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '')
    const label = `${parsed.hostname.replace(/^www\./, '')}${path}`
    const normalized = label.toLocaleLowerCase()
    const matchIndex = matchTerms
      .map((term) => term.trim().toLocaleLowerCase())
      .filter((term) => term.length > 1)
      .map((term) => normalized.indexOf(term))
      .find((index) => index >= 0)
    const maximumLength = 64
    if (matchIndex !== undefined && label.length > maximumLength) {
      const start = Math.max(0, Math.min(matchIndex - 18, label.length - maximumLength))
      const end = Math.min(label.length, start + maximumLength)
      return hideMarkdownPreviewArtifacts(`${start > 0 ? '…' : ''}${label.slice(start, end).trimEnd()}${end < label.length ? '…' : ''}`)
    }
    return hideMarkdownPreviewArtifacts(label.length > maximumLength ? `${label.slice(0, maximumLength - 1).trimEnd()}…` : label)
  } catch {
    return hideMarkdownPreviewArtifacts(url.originalUrl)
  }
}

function textFingerprint(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[\p{P}\p{S}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function hasDistinctDescription(url: UrlEntity, title: string): boolean {
  if (!url.description) return false
  const description = textFingerprint(url.description)
  return Boolean(description)
    && description !== textFingerprint(title)
    && isUsableUrlTitle(url, url.description)
}

function displayTitle(title: string): string {
  const maximumLength = APP_SETTINGS.links.previewTitleMaxChars
  const cleaned = hideMarkdownPreviewArtifacts(title).trim()
  return cleaned.length > maximumLength
    ? `${cleaned.slice(0, maximumLength).trimEnd()}…`
    : cleaned
}

export const LinkPreview = memo(function LinkPreview({
  readOnly = false,
  contentType,
  fallbackTitle,
  hideTitle = false,
  matchTerms = [],
  metadataStatus,
  onRetryMetadata,
  onRemove,
  preserveArtworkOnFailure = false,
  removing = false,
  url,
  variant = 'full',
}: LinkPreviewProps) {
  const [copied, setCopied] = useState(false)
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null)
  const [loadedImageUrl, setLoadedImageUrl] = useState<string | null>(null)
  const previewRef = useRef<HTMLElement>(null)
  const previousPreview = useRef<{ url: string; pending: boolean } | null>(null)
  const enrichingUrl = useRef<string | null>(null)
  const presentation = linkPreviewPresentation(url, contentType)
  const resolvedMetadataStatus = metadataStatus === undefined
    ? deriveLinkMetadataStatus(url)
    : metadataStatus
  const canRetryMetadata = !readOnly && Boolean(onRetryMetadata && hasExhaustedLinkMetadataRetries(url))
  const resolvedTitle = isUsableUrlTitle(url, url.title)
    ? urlDisplayTitle(url)
    : fallbackTitle?.trim() || urlDisplayTitle(url)
  const source = textFingerprint(presentation.provider) === textFingerprint(resolvedTitle)
    ? null
    : presentation.provider
  const title = displayTitle(resolvedTitle)
  const resolvedImageUrl = linkPreviewImageUrl(url.imageUrl)
  const imageFailed = Boolean(resolvedImageUrl && failedImageUrl === resolvedImageUrl)
  const artworkSlotAvailable = Boolean(
    resolvedImageUrl
    && (preserveArtworkOnFailure || !imageFailed)
    && hasSpecificLinkImage(url, url.imageUrl),
  )
  const metadataPending = resolvedMetadataStatus !== null && !hasExhaustedLinkMetadataRetries(url)
  if (metadataPending) enrichingUrl.current = url.originalUrl
  const previewPending = metadataPending || (enrichingUrl.current === url.originalUrl && artworkSlotAvailable && !imageFailed && loadedImageUrl !== resolvedImageUrl)
  useLayoutEffect(() => {
    const previous = previousPreview.current
    previousPreview.current = { url: url.originalUrl, pending: previewPending }
    if (!previewPending) enrichingUrl.current = null
    const element = previewRef.current
    if (!element || !previous?.pending || previous.url !== url.originalUrl || previewPending) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || !element.animate) return
    const initialScale = Number(getComputedStyle(element).getPropertyValue('--studio-link-reveal-scale')) || 1
    const animation = element.animate(previewSpringFrames(initialScale), {
      duration: APP_SETTINGS.motion.linkPreview.revealMs,
      easing: 'linear',
    })
    return () => animation.cancel()
  }, [previewPending, url.originalUrl])
  const description = hasDistinctDescription(url, resolvedTitle)
    ? hideMarkdownPreviewArtifacts(url.description ?? '')
    : null
  const mediaDetails = [
    url.author,
    typeof url.durationSeconds === 'number' ? formatDuration(url.durationSeconds) : null,
  ].filter((detail): detail is string => Boolean(detail))
  const videoDetails = presentation.category === 'video' ? mediaDetails : []
  const details = [
    ...(presentation.category === 'video' ? [] : mediaDetails),
    typeof url.publishedAt === 'number'
      ? new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(url.publishedAt)
      : null,
  ].filter((detail): detail is string => Boolean(detail))

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 1_500)
    return () => window.clearTimeout(timer)
  }, [copied])

  const copyUrl = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    try {
      await copyText(url.originalUrl)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const removeUrl = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (removing) return
    onRemove?.(url)
  }

  const retryMetadata = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onRetryMetadata?.(url)
  }

  return (
    <article
      ref={previewRef}
      className={`link-preview link-preview--${variant} link-preview--${presentation.category}${removing ? ' link-preview--removing' : ''}`}
      data-link-category={presentation.category}
      data-link-image={artworkSlotAvailable ? 'available' : 'fallback'}
      data-link-image-failed={imageFailed || undefined}
      data-link-pending={previewPending || undefined}
      data-link-provider={presentation.provider.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
      aria-label={`${presentation.label} preview for ${title}`}
    >
      <span className="link-preview__ambient" aria-hidden="true" />
      {resolvedImageUrl && artworkSlotAvailable && imageFailed ? (
        <span className="link-preview__artwork link-preview__artwork--unavailable" aria-hidden="true">
          <Icon name={presentation.icon} />
        </span>
      ) : resolvedImageUrl && artworkSlotAvailable ? (
        <img
          className="link-preview__artwork"
          src={resolvedImageUrl}
          alt=""
          crossOrigin="anonymous"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailedImageUrl(resolvedImageUrl)}
          onLoad={() => setLoadedImageUrl(resolvedImageUrl)}
        />
      ) : null}
      <span className="link-preview__scrim" aria-hidden="true" />
      <div className="link-preview__content">
        <div className="link-preview__body">
          <span className="link-preview__meta">
            <span className={`link-preview__category link-preview__category--${presentation.category}`}>
              <Icon name={presentation.icon} />
              <span>{presentation.label}</span>
            </span>
            {source ? <span className="link-preview__provider">{source}</span> : null}
            {canRetryMetadata && resolvedMetadataStatus === 'pending' ? (
              <button
                type="button"
                className="btn btn-ghost btn-xs link-preview__metadata-retry"
                aria-label="Retry fetching link details"
                title="Retry fetching link details"
                onClick={retryMetadata}
              >
                <Icon name="undo" />
              </button>
            ) : null}
            {details.length > 0 ? <span>· {details.join(' · ')}</span> : null}
          </span>
          {hideTitle ? null : (
            <>
              <strong className={previewPending ? 't-shimmer' : undefined} data-text={previewPending ? title : undefined}>{title}</strong>
              {videoDetails.length > 0 ? (
                <span className="link-preview__video-details">{videoDetails.join(' · ')}</span>
              ) : null}
            </>
          )}
          {description ? <span className="link-preview__description">{description}</span> : (
            <span className="link-preview__description link-preview__description--url">{displayUrl(url, matchTerms)}</span>
          )}
        </div>

        {!readOnly && <div className="link-preview__actions" aria-label="Link actions">
          {onRemove ? (
            <>
              <button
                type="button"
                className="btn btn-ghost btn-square link-preview__remove"
                aria-label={`Remove ${title} from note`}
                disabled={removing}
                title="Remove link from note"
                onClick={removeUrl}
              >
                <Icon name="trash" />
                <span>Remove</span>
              </button>
              <button
                type="button"
                className={copied
                  ? 'btn btn-ghost btn-square link-preview__copy link-preview__copy--copied'
                  : 'btn btn-ghost btn-square link-preview__copy'}
                aria-label={copied ? 'Link copied' : `Copy link to ${title}`}
                title={copied ? 'Copied' : 'Copy link'}
                onClick={copyUrl}
              >
                <Icon name={copied ? 'check' : 'copy'} />
                <span>{copied ? 'Copied' : 'Copy URL'}</span>
              </button>
            </>
          ) : null}
          <a
            className="btn btn-ghost btn-square link-preview__visit"
            href={url.originalUrl}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`${presentation.actionLabel} ${title}`}
            onClick={(event) => event.stopPropagation()}
          >
            <Icon name="external" />
            <span>Open</span>
          </a>
        </div>}
      </div>
    </article>
  )
})
